import { CircleQuestionMarkIcon } from 'lucide-react';

import type { DocsKey } from '@/lib/docs';
import { docsUrl } from '@/lib/docs';
import { cn } from '@/lib/utils';

/**
 * A quiet way into the documentation, next to the thing it documents
 * (PLAN-DOCS A3/B4, decision D-DX4).
 *
 * Deliberately small and muted. It sits in panel headers and section titles
 * that a brewer reads past forty times a week, so it has to be findable when
 * wanted and invisible when not: a 12-pixel outline question mark in
 * `text-muted-foreground`, which comes up to full contrast on hover and focus.
 * Nothing about it competes with the **Add** button beside it.
 *
 * It is an `<a target="_blank">`, following the app's existing idiom for an
 * outbound link (`settings/fermentation-devices.tsx`), because the docs are a
 * different site: opening them in the tab holding an unsaved brew day would
 * be wrong even though nothing here is ever unsaved.
 *
 * `label` names the *surface*, not the page — "Fermentables", not "the
 * fermentables docs" — because it is read aloud inside a fixed sentence:
 * "Read the docs for Fermentables". Screen-reader users get the same sentence
 * on every one of these, which is what makes them skippable.
 */
export interface DocsLinkProps {
  /** Which page to open. A key of `DOCS`, so a rename fails to compile. */
  readonly docs: DocsKey;
  /** The surface's own name, e.g. `Fermentables`. */
  readonly label: string;
  /** An anchor within the page, without its `#`. */
  readonly anchor?: string;
  readonly className?: string;
}

export function DocsLink({ docs, label, anchor, className }: DocsLinkProps) {
  const description = `Read the docs for ${label}`;
  return (
    <a
      data-slot="docs-link"
      href={docsUrl(docs, anchor)}
      target="_blank"
      rel="noreferrer"
      aria-label={description}
      title={description}
      className={cn(
        'inline-flex shrink-0 items-center text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 rounded-sm',
        className,
      )}
    >
      <CircleQuestionMarkIcon className="size-3.5" aria-hidden="true" />
    </a>
  );
}
